import { Logo } from "components";

type LegalTextBlock = { type: "h2" | "h3" | "p"; text: string };
type LegalBlock = LegalTextBlock | { type: "list"; items: string[] };

const lastUpdated = "July 28, 2026";
const supportEmail = "gataastudio@gmail.com";

const blocks: LegalBlock[] = [
  { type: "h2", text: "1. Introduction" },
  {
    type: "p",
    text: "LinksRoom is developed by Gataa Studio. This Privacy Policy explains how we collect, use, disclose, store, and protect information when you use LinksRoom, including our mobile applications, website, public profile pages, rooms, communication tools, analytics, paid features, and related services.",
  },
  {
    type: "p",
    text: "By using LinksRoom, you agree to the collection and use of information as described in this Privacy Policy. If you do not agree, you should not use LinksRoom.",
  },
  { type: "h2", text: "2. Information We Collect" },
  {
    type: "p",
    text: "The information we collect depends on how you use LinksRoom, which features you choose to use, your device permissions, and your account settings.",
  },
  { type: "h3", text: "2.1 Account and profile information" },
  {
    type: "p",
    text: "We may collect information you provide when creating or managing your account or profile, including:",
  },
  {
    type: "list",
    items: [
      "Name, username, display name, LinksRoom ID, and user ID.",
      "Email address and authentication information.",
      "Profile photo, avatar, bio, birthday or date of birth, gender, profile settings, language, and preferences.",
      "Other information you choose to add to your profile or account.",
    ],
  },
  { type: "h3", text: "2.2 Content you create, upload, import, send, or share" },
  {
    type: "p",
    text: "We may collect and store content you create, upload, import, save, publish, send, reshare, or otherwise make available through LinksRoom, including:",
  },
  {
    type: "list",
    items: [
      "Links, URLs, bookmark imports, captions, descriptions, categories, visibility settings, and metadata.",
      "Files and documents.",
      "Photos, profile images, uploaded images, banners, post images, article images, product images, and other visual content.",
      "Audio files and related metadata.",
      "Rooms, room About information, room members, invitations, requests, public room links, QR links, and room content.",
      "Posts, articles, magazines, products, stores, prices, categories, order-related information, and banners.",
      "Direct messages, chat content, replies, reactions, conversation information, invitations, requests, and other in-app messages.",
      "Likes, reshares, saves, follows, blocks, reports, and other engagement or safety actions.",
    ],
  },
  { type: "h3", text: "2.3 Location information" },
  {
    type: "p",
    text: "If you use location-based features, such as Local Communication or nearby profile discovery, we may collect approximate location and, where permitted by you and your device settings, precise location.",
  },
  {
    type: "p",
    text: "We may also infer approximate location from technical information such as IP address. You can control device-level location permissions through your device settings, but some location-based features may not work without location access.",
  },
  { type: "h3", text: "2.4 Usage, activity, and interaction data" },
  {
    type: "p",
    text: "We may collect information about how you use LinksRoom, including:",
  },
  {
    type: "list",
    items: [
      "Profile views, link views, file interactions, clicks, content views, search history inside the app, and feature usage.",
      "App interactions, room interactions, message activity, requests, invitations, likes, reshares, blocks, reports, and other user actions.",
      "Analytics, performance, trends, preferences, and interaction statistics connected to your account, profile, links, rooms, content, and store.",
    ],
  },
  { type: "h3", text: "2.5 Device, technical, and diagnostic data" },
  {
    type: "p",
    text: "We may collect device and technical information, including:",
  },
  {
    type: "list",
    items: [
      "Device type, operating system, browser type, app version, language, time zone, IP address, network status, and device or other identifiers.",
      "Crash logs, diagnostics, error logs, performance data, security logs, and similar technical information.",
    ],
  },
  { type: "h3", text: "2.6 Third-party sign-in information" },
  {
    type: "p",
    text: "If you sign in with a third-party provider such as Google, Apple, Facebook, or another provider, we may receive information from that provider, such as your name, email address, profile image, authentication identifier, and related account information, depending on your settings with that provider.",
  },
  { type: "h3", text: "2.7 Payment and subscription information" },
  {
    type: "p",
    text: "If you purchase a paid plan or subscription, we may receive purchase history, subscription status, plan type, renewal status, expiration status, transaction references, and similar purchase information from Apple App Store, Google Play, or another payment provider.",
  },
  {
    type: "p",
    text: "We do not intentionally collect or store full payment card numbers, bank account numbers, or full payment credentials when purchases are processed by app stores or third-party payment providers.",
  },
  { type: "h3", text: "2.8 Support and communications" },
  {
    type: "p",
    text: "If you contact us for support, report a problem, appeal an enforcement action, or communicate with us, we may collect your contact information, messages, attachments, account information, and any details needed to respond.",
  },
  { type: "h2", text: "3. How We Collect Information" },
  { type: "p", text: "We collect information in the following ways:" },
  {
    type: "list",
    items: [
      "Directly from you when you create an account, edit your profile, upload content, import bookmarks, create rooms, send messages, choose visibility settings, use stores, contact support, or use LinksRoom features.",
      "Automatically when you use LinksRoom, such as when we record views, clicks, searches, interactions, logs, diagnostics, location signals, and device information.",
      "From third-party services when you choose to sign in, connect, subscribe, pay, or interact through those services.",
      "From other users when they send messages, invite you, request access, report content, block users, reshare content, interact with your content, or include you in rooms or communications.",
    ],
  },
  { type: "h2", text: "4. How We Use Information" },
  { type: "p", text: "We use the information we collect to:" },
  {
    type: "list",
    items: [
      "Create, manage, authenticate, and secure your account.",
      "Provide LinksRoom features, including profiles, link management, file sharing, bookmark import, rooms, public room links, QR links, stores, products, orders, posts, articles, magazines, banners, welcome messages, messaging, notifications, analytics, promotions, reshares, and visibility controls.",
      "Show profile pages, public links, shared content, room content, search results, Explore content, and other content according to your settings and app features.",
      "Send and receive direct messages, replies, reactions, invitations, requests, order updates, room updates, and notifications.",
      "Provide Local Communication and other location-based features when enabled.",
      "Show view counts, click counts, engagement statistics, charts, performance metrics, and other insights connected to your content.",
      "Personalize your experience, remember settings, improve search, and recommend or display relevant content.",
      "Detect, review, prevent, and respond to spam, abuse, fraud, scams, harassment, unsafe content, prohibited visual content, security incidents, and violations of our Terms.",
      "Review uploaded images, visual content, files, messages, profiles, rooms, products, and other content for safety, integrity, moderation, and policy enforcement.",
      "Process purchases, subscriptions, plan access, account limits, and paid feature availability.",
      "Provide support, respond to requests, communicate important service updates, and send administrative messages.",
      "Maintain, troubleshoot, test, analyze, and improve LinksRoom.",
      "Comply with legal obligations, enforce our Terms, protect rights and safety, and respond to lawful requests.",
    ],
  },
  { type: "h2", text: "5. Content Visibility and Access Controls" },
  {
    type: "p",
    text: "LinksRoom allows you to manage who can access certain links, files, profile content, rooms, posts, articles, products, messages, and other content when visibility controls are available.",
  },
  {
    type: "p",
    text: "Some information may be visible to others depending on your profile settings, sharing choices, public links, rooms, room invitations, public room links, QR links, reshared content, Explore activity, stores, magazines, and other features you use.",
  },
  {
    type: "p",
    text: "Public content may be accessible to people outside LinksRoom or without a LinksRoom account. If another user has already viewed, saved, reshared, copied, screenshotted, or accessed content you made available, changing your settings later may not remove copies or records outside our control.",
  },
  { type: "h2", text: "6. Messages and Communications" },
  {
    type: "p",
    text: "LinksRoom may store in-app messages, chat content, replies, reactions, conversation metadata, request messages, invitation messages, and related communication information to provide messaging, safety, synchronization, moderation, support, and account features.",
  },
  {
    type: "p",
    text: "Messages may remain available to other conversation participants even if you delete your account or remove content, unless applicable law requires deletion or our systems support deletion for that specific message or record.",
  },
  { type: "h2", text: "7. Content Moderation, Visual Review, Reports, and Blocking" },
  {
    type: "p",
    text: "We may use automated tools, manual review, service providers, or a combination of these methods to review content and activity, including uploaded images and other visual content, to detect unsafe, illegal, abusive, sexually explicit, violent, fraudulent, infringing, spammy, or otherwise prohibited content.",
  },
  {
    type: "p",
    text: "If you report content or another user, we may collect and review the report, reported content, account information, messages, room information, device or technical data, and related activity needed to investigate and enforce our Terms.",
  },
  {
    type: "p",
    text: "If you block another user, we may store the block relationship and related metadata so that LinksRoom can apply the block. Blocking may limit future interactions, but it may not erase prior messages, notifications, reports, orders, room records, or content already shared with others.",
  },
  { type: "h2", text: "8. How We Share Information" },
  {
    type: "p",
    text: "We do not sell your personal information. We may share information only in limited situations, including:",
  },
  {
    type: "list",
    items: [
      "With people who can access content based on your sharing choices, room membership, visibility settings, public profile settings, public links, public rooms, messages, reshares, or other app features.",
      "With service providers that help us operate LinksRoom, such as hosting, storage, authentication, cloud infrastructure, analytics, crash reporting, diagnostics, content moderation, security, payments, subscriptions, notifications, email, and support providers.",
      "With app stores and payment providers when needed to process purchases, subscriptions, renewals, cancellations, refunds, fraud prevention, or account entitlement checks.",
      "With third-party sign-in providers when you choose to sign in or connect your account through them.",
      "When required by law, legal process, court order, subpoena, or valid government request.",
      "To protect the rights, safety, security, or integrity of LinksRoom, users, Gataa Studio, or others.",
      "To investigate, prevent, or respond to fraud, abuse, security issues, policy violations, or harmful activity.",
      "In connection with a business transfer, such as a merger, acquisition, financing, restructuring, bankruptcy, or sale of assets.",
    ],
  },
  { type: "h2", text: "9. Third-Party Services and Links" },
  {
    type: "p",
    text: "LinksRoom may include links to third-party websites, profiles, services, payment providers, social networks, app stores, or content. LinksRoom may also allow sign-in through third-party providers such as Google, Apple, Facebook, or similar providers.",
  },
  {
    type: "p",
    text: "Third-party services have their own privacy policies and practices. We are not responsible for their content, security, availability, or data practices.",
  },
  { type: "h2", text: "10. Data Retention" },
  {
    type: "p",
    text: "We keep information for as long as needed to provide LinksRoom, maintain your account, operate features, preserve your profile and content, provide messaging, maintain rooms, process orders, support analytics, enforce limits, prevent abuse, comply with legal obligations, resolve disputes, enforce our Terms, and support legitimate operational needs.",
  },
  {
    type: "p",
    text: "In general, we retain your account information, profile information, avatar, email address, links, files, messages, chat records, rooms, stores, products, posts, articles, interactions, and related account content for as long as your account remains active or as long as needed to provide the service.",
  },
  {
    type: "p",
    text: "If you delete your account or request account deletion, we will take reasonable steps to delete or anonymize personal information associated with your account, unless we need to retain certain information for legal compliance, security, fraud prevention, abuse prevention, backups, dispute resolution, transaction records, moderation records, enforcement history, or legitimate operational needs.",
  },
  {
    type: "p",
    text: "Deleted content and account information may remain for a limited time in backups, logs, caches, or archival systems before being overwritten or deleted according to our normal retention processes.",
  },
  { type: "h2", text: "11. Account Deletion" },
  {
    type: "p",
    text: `You may request deletion of your account through available in-app tools or by contacting us at ${supportEmail}.`,
  },
  {
    type: "p",
    text: "When an account deletion request is processed, we will delete or anonymize associated personal data where required, including account and profile information, subject to retention exceptions described in this Privacy Policy and applicable law.",
  },
  {
    type: "p",
    text: "Some information may remain visible or retained where necessary, including:",
  },
  {
    type: "list",
    items: [
      "Messages or conversation records visible to other participants.",
      "Content reshared, copied, saved, screenshotted, exported, or accessed by other users before deletion.",
      "Transaction, subscription, order, support, security, fraud prevention, or legal records.",
      "Moderation, report, block, or enforcement records needed to protect the platform and users.",
      "Data stored in backups for a limited period.",
    ],
  },
  { type: "h2", text: "12. Security" },
  {
    type: "p",
    text: "We use reasonable technical and organizational measures designed to protect information from unauthorized access, loss, misuse, alteration, or disclosure. These measures may include access controls, authentication, monitoring, encryption in transit where appropriate, security logging, and operational safeguards.",
  },
  {
    type: "p",
    text: "However, no online service can be guaranteed to be completely secure. You are responsible for using a strong password, protecting your account, and controlling what you choose to share.",
  },
  { type: "h2", text: "13. Your Choices and Rights" },
  {
    type: "p",
    text: "Depending on where you live, you may have rights to access, update, correct, delete, restrict, export, or object to certain uses of your personal information.",
  },
  {
    type: "p",
    text: "You can update some account and profile information directly in LinksRoom. You can also control certain visibility settings, notification settings, location permissions, profile settings, room settings, blocks, and content settings through the app or your device.",
  },
  {
    type: "p",
    text: `For privacy requests, contact us at ${supportEmail}. We may need to verify your identity before processing certain requests.`,
  },
  { type: "h2", text: "14. Children's Privacy" },
  {
    type: "p",
    text: "LinksRoom is intended for users who are at least 18 years old, or the age of legal majority in their jurisdiction. We do not knowingly collect personal information from children. If you believe a child has provided personal information to LinksRoom, please contact us.",
  },
  { type: "h2", text: "15. International Use" },
  {
    type: "p",
    text: "LinksRoom may be accessed from different countries. Your information may be processed or stored in countries other than the country where you live. Where required, we take reasonable steps to protect information consistent with this Privacy Policy.",
  },
  { type: "h2", text: "16. App Store and Google Play Privacy Disclosures" },
  {
    type: "p",
    text: "App stores may require separate privacy or data safety disclosures. These disclosures should be consistent with this Privacy Policy and with the data LinksRoom collects and uses.",
  },
  {
    type: "p",
    text: "Based on LinksRoom features, app store disclosures may include categories such as location, personal information, purchase history, in-app messages, photos, audio files, files and documents, app activity, search history, user-generated content, diagnostics, crash logs, and device or other identifiers.",
  },
  {
    type: "p",
    text: "You are responsible for reviewing the final app behavior, SDKs, permissions, and platform forms to make sure store disclosures are complete and accurate.",
  },
  { type: "h2", text: "17. Changes to This Privacy Policy" },
  {
    type: "p",
    text: "We may update this Privacy Policy from time to time. If changes are significant, we may notify users through the app, website, email, app store listing, or another reasonable method. The updated version will be effective when posted, unless stated otherwise.",
  },
  { type: "h2", text: "18. Contact Us" },
  {
    type: "p",
    text: `If you have questions, requests, or concerns about this Privacy Policy or how LinksRoom handles information, contact Gataa Studio at ${supportEmail}.`,
  },
];

const createLegalId = (text: string, index: number) =>
  `${index}-${text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")}`;

const getHeadingId = (block: LegalTextBlock, index: number) =>
  createLegalId(block.text, index);

const tableOfContents = blocks
  .map((block, index) => {
    if (block.type !== "h2" && block.type !== "h3") {
      return null;
    }

    return {
      id: getHeadingId(block, index),
      level: block.type,
      text: block.text,
    };
  })
  .filter(
    (item): item is { id: string; level: "h2" | "h3"; text: string } =>
      Boolean(item)
  );

export default function Privacy() {
  return (
    <>
      <style>
        {`
body {
  margin: 0;
  background-color: #f4f4f4;
}

.legal-page {
  min-height: 100vh;
  padding: 24px 16px;
  font-family: Arial, sans-serif;
  color: #333;
}

.legal-container {
  max-width: 840px;
  margin: 0 auto;
  padding: 28px;
  background-color: #fff;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
}

.legal-header {
  text-align: center;
  padding-bottom: 24px;
  border-bottom: 1px solid #ddd;
}

.legal-logo {
  width: 150px;
  margin: 0 auto 12px;
}

.legal-title {
  margin: 0;
  font-size: 26px;
  line-height: 1.25;
  color: #222;
}

.legal-subtitle {
  margin: 8px 0 0;
  font-size: 15px;
  color: #666;
}

.legal-main {
  padding: 24px 0 0;
}

.legal-toc {
  margin: 24px 0 0;
  padding: 18px;
  background-color: #f8fafc;
  border: 1px solid #e5e7eb;
}

.legal-toc-title {
  margin: 0 0 12px;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.4;
  color: #222;
}

.legal-toc-list {
  display: grid;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.legal-toc-item {
  margin-left: 0;
}

.legal-toc-item-h3 {
  margin-left: 18px;
}

.legal-toc-link,
.legal-heading-link {
  color: inherit;
  text-decoration: none;
}

.legal-toc-link {
  display: inline-block;
  font-size: 15px;
  line-height: 1.45;
  color: #2563eb;
}

.legal-toc-link:hover,
.legal-heading-link:hover {
  text-decoration: underline;
}

.legal-main h2,
.legal-main h3 {
  scroll-margin-top: 20px;
}

.legal-main h2 {
  margin: 28px 0 10px;
  font-size: 20px;
  line-height: 1.35;
  color: #222;
}

.legal-main h2:first-child {
  margin-top: 0;
}

.legal-main h3 {
  margin: 18px 0 8px;
  font-size: 17px;
  line-height: 1.35;
  color: #333;
}

.legal-main p,
.legal-main li {
  font-size: 16px;
  line-height: 1.65;
  color: #5f5f5f;
}

.legal-main p {
  margin: 0 0 14px;
}

.legal-main ul {
  margin: 0 0 14px;
  padding-left: 22px;
}

@media (max-width: 600px) {
  .legal-page {
    padding: 12px;
  }

  .legal-container {
    padding: 18px;
  }

  .legal-title {
    font-size: 23px;
  }
}
`}
      </style>

      <div className="legal-page">
        <div className="legal-container">
          <header className="legal-header">
            <Logo variant="logo-text-row-dark" className="legal-logo" />
            <h1 className="legal-title">LinksRoom Privacy Policy</h1>
            <p className="legal-subtitle">Last updated: {lastUpdated}</p>
          </header>

          <nav className="legal-toc" aria-label="Privacy sections">
            <p className="legal-toc-title">Contents</p>
            <ul className="legal-toc-list">
              {tableOfContents.map((item) => (
                <li
                  key={item.id}
                  className={`legal-toc-item legal-toc-item-${item.level}`}
                >
                  <a className="legal-toc-link" href={`#${item.id}`}>
                    {item.text}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <main className="legal-main">
            {blocks.map((block, index) => {
              if (block.type === "list") {
                return (
                  <ul key={index}>
                    {block.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                );
              }

              if (block.type === "p") {
                return <p key={index}>{block.text}</p>;
              }

              const id = getHeadingId(block, index);
              const Component = block.type;
              return (
                <Component id={id} key={index}>
                  <a className="legal-heading-link" href={`#${id}`}>
                    {block.text}
                  </a>
                </Component>
              );
            })}
          </main>
        </div>
      </div>
    </>
  );
}
